/**
 * M'art Microlit Product Configuration
 * =====================================
 * All product data, packaging, and coverage rates from mart-mineral.com
 * This file is easily editable for future updates
 */

// Packaging sizes in grams (updated per mart-mineral.com)
export const PACKAGING_SIZES = {
  microlit: [
    { size: 150, unit: 'g', label: '150 גרם', pricePerKg: 400 },
    { size: 250, unit: 'g', label: '250 גרם', pricePerKg: 400 },
  ],
  primerSolan: [
    { size: 150, unit: 'g', label: '150 גרם', pricePerLiter: 200 },
    { size: 250, unit: 'g', label: '250 גרם', pricePerLiter: 200 },
  ],
  primerQuartz: [
    { size: 150, unit: 'g', label: '150 גרם', pricePerKg: 150 },
    { size: 250, unit: 'g', label: '250 גרם', pricePerKg: 150 },
  ],
  sealer: [
    { size: 100, unit: 'ml', label: '100 מ"ל', pricePerLiter: 150 },
  ],
};

// Coverage rates (grams per m²) - Updated per mart-mineral.com
export const COVERAGE_RATES = {
  microlit: {
    perLayer: 1000, // 1 kg/m² for diluted (smooth finish)
    thickCoat: 2000, // 2 kg/m² for thick (textured finish)
    minLayers: 1,
    maxLayers: 3,
    recommendedLayers: 2,
  },
  primerSolan: {
    perLayer: 333, // 1 kg per 3 m² = 333g/m² (for absorbent surfaces)
  },
  primerQuartz: {
    perLayer: 1000, // 1 kg per 1 m² (for non-absorbent surfaces)
  },
  sealer: {
    perLayer: 200, // 1 liter per 5 m² = 200ml/m²
  },
};

// Pricing (NIS)
export const PRICING = {
  primerQuartz: 150, // ₪ per kg
  primerSolan: 200, // ₪ per liter
  microlit: 400, // ₪ per kg (both thick and diluted)
  sealer: 150, // ₪ per liter
};

// Preset creation sizes with average surface areas (in m²)
export const CREATION_SIZES = {
  apple: {
    id: 'apple',
    nameHe: '🍎 תפוח',
    nameEn: '🍎 Apple',
    descriptionHe: 'גודל ממוצע של תפוח',
    descriptionEn: 'Average apple size',
    areaCm2: 200, // ~200 cm²
    areaM2: 0.02,
    icon: 'Apple',
  },
  basketball: {
    id: 'basketball',
    nameHe: '🏀 כדורסל',
    nameEn: '🏀 Basketball',
    descriptionHe: 'כדור בגודל סטנדרטי',
    descriptionEn: 'Standard basketball size',
    areaCm2: 1810, // ~1810 cm² (diameter 24cm)
    areaM2: 0.18,
    icon: 'Circle',
  },
  bottle: {
    id: 'bottle',
    nameHe: '🍾 בקבוק 1.5 ליטר',
    nameEn: '🍾 1.5L Bottle',
    descriptionHe: 'בקבוק שתייה גדול',
    descriptionEn: 'Large drink bottle',
    areaCm2: 900, // ~900 cm²
    areaM2: 0.09,
    icon: 'Wine',
  },
  coffeeTable: {
    id: 'coffee-table',
    nameHe: '🛋️ שולחן קפה',
    nameEn: '🛋️ Coffee Table',
    descriptionHe: 'שולחן סלון ממוצע',
    descriptionEn: 'Average coffee table top',
    areaCm2: 6000, // ~6000 cm² (60x100cm)
    areaM2: 0.6,
    icon: 'Square',
  },
  custom: {
    id: 'custom',
    nameHe: '📏 מידה מותאמת אישית',
    nameEn: '📏 Custom Size',
    descriptionHe: 'הזן את השטח בעצמך',
    descriptionEn: 'Enter your own dimensions',
    areaCm2: 0,
    areaM2: 0,
    icon: 'Ruler',
  },
};

// ==============================
// COLOR SYSTEM
// ==============================

// Color system types
export const COLOR_SYSTEMS = {
  natural: {
    id: 'natural',
    nameHe: 'ציפויים מינרליים טבעיים',
    nameEn: 'Natural Mineral Coatings',
    descriptionHe: 'צבע מגיע מהמינרל עצמו - ללא פיגמנטים מלאכותיים',
    descriptionEn: 'Color derived from natural mineral composition – no synthetic pigments',
  },
  tinted: {
    id: 'tinted',
    nameHe: 'צבעים מינרליים',
    nameEn: 'Tinted Mineral Colors',
    descriptionHe: 'מגוון רחב של צבעים במלאי - ניתנים לגיוון לפי דרישה',
    descriptionEn: 'Wide range of colors in stock - can be tinted on demand',
  },
};

// Natural Mineral Coatings (6 colors) - NO artificial pigments
export const NATURAL_MINERALS = {
  betonite: {
    id: 'MM-N001',
    code: 'MM-N001',
    nameHe: 'בטונייט',
    nameEn: 'Betonite',
    descriptionHe: 'גוון בטון אדריכלי בהיר',
    descriptionEn: 'Light architectural concrete tone',
    hex: '#C5C5C0',
    gradient: 'linear-gradient(145deg, #D0D0CB 0%, #ABABAB 100%)',
    texture: true,
  },
  betonda: {
    id: 'MM-N002',
    code: 'MM-N002',
    nameHe: 'בטונדה',
    nameEn: 'Betonda',
    descriptionHe: 'גוון בטון כהה',
    descriptionEn: 'Dark concrete tone',
    hex: '#6B6B6B',
    gradient: 'linear-gradient(145deg, #787878 0%, #525252 100%)',
    texture: true,
  },
  mika: {
    id: 'MM-N003',
    code: 'MM-N003',
    nameHe: 'מיקה',
    nameEn: 'Mika',
    descriptionHe: 'גוון בז\' מינרלי טבעי',
    descriptionEn: 'Natural beige mineral tone',
    hex: '#D4C4A8',
    gradient: 'linear-gradient(145deg, #E0D0B8 0%, #C0B090 100%)',
    texture: true,
  },
  titanium: {
    id: 'MM-N004',
    code: 'MM-N004',
    nameHe: 'טיטניום',
    nameEn: 'Titanium',
    descriptionHe: 'גוון לבן מינרלי טבעי',
    descriptionEn: 'Natural white mineral tone',
    hex: '#F5F5F0',
    gradient: 'linear-gradient(145deg, #FAFAF5 0%, #E8E8E0 100%)',
    texture: true,
  },
  clay: {
    id: 'MM-N005',
    code: 'MM-N005',
    nameHe: 'חרסית ממשית',
    nameEn: 'Clay (Terracotta)',
    descriptionHe: 'גוון חימר טרקוטה טבעי',
    descriptionEn: 'Natural terracotta clay tone',
    hex: '#C67B5C',
    gradient: 'linear-gradient(145deg, #D4896A 0%, #A86545 100%)',
    texture: true,
  },
  yatir: {
    id: 'MM-N006',
    code: 'MM-N006',
    nameHe: 'יתיר',
    nameEn: 'Yatir',
    descriptionHe: 'גוון צהוב מינרלי טבעי',
    descriptionEn: 'Natural yellow mineral tone',
    hex: '#D4A060',
    gradient: 'linear-gradient(145deg, #DCAA5A 0%, #C89838 100%)',
    texture: true,
  },
};

// Tinted Mineral Colors (21 colors) - Open palette
export const TINTED_COLORS = {
  'MM-0101': { id: 'MM-0101', code: 'MM-0101', nameHe: 'לבן ליים', nameEn: 'Lime White', hex: '#F7F5E8' },
  'MM-0112': { id: 'MM-0112', code: 'MM-0112', nameHe: 'לבן קרם', nameEn: 'Cream White', hex: '#EFEBE0' },
  'MM-0123': { id: 'MM-0123', code: 'MM-0123', nameHe: 'לבן קוורץ', nameEn: 'Quartz White', hex: '#DEDEDE' },
  'MM-0134': { id: 'MM-0134', code: 'MM-0134', nameHe: 'לבן פנינה', nameEn: 'Pearl White', hex: '#E3D9C6' },
  'MM-0145': { id: 'MM-0145', code: 'MM-0145', nameHe: 'שנהב חולי', nameEn: 'Sandy Ivory', hex: '#E6D2B5' },
  
  'MM-0201': { id: 'MM-0201', code: 'MM-0201', nameHe: 'אפור גרניט', nameEn: 'Granite Grey', hex: '#D0D0D0' },
  'MM-0212': { id: 'MM-0212', code: 'MM-0212', nameHe: 'אפור צדף', nameEn: 'Shell Grey', hex: '#C5C7C4' },
  'MM-0223': { id: 'MM-0223', code: 'MM-0223', nameHe: 'אפור משי', nameEn: 'Silk Grey', hex: '#B8B8A8' },
  'MM-0234': { id: 'MM-0234', code: 'MM-0234', nameHe: 'אפור חלוק', nameEn: 'Pebble Grey', hex: '#B5B0A1' },
  'MM-0245': { id: 'MM-0245', code: 'MM-0245', nameHe: 'אפור אגט', nameEn: 'Agate Grey', hex: '#A9ADA8' },
  
  'MM-0301': { id: 'MM-0301', code: 'MM-0301', nameHe: 'בז\' אדמה', nameEn: 'Earth Beige', hex: '#A08C7A' },
  'MM-0312': { id: 'MM-0312', code: 'MM-0312', nameHe: 'בז\' מיקה', nameEn: 'Mika Beige', hex: '#C7A688' },
  
  'MM-0323': { id: 'MM-0323', code: 'MM-0323', nameHe: 'אפור סיליקה', nameEn: 'Silica Grey', hex: '#8F8A7B' },
  'MM-0334': { id: 'MM-0334', code: 'MM-0334', nameHe: 'אפור פלטינה', nameEn: 'Platinum Grey', hex: '#949089' },
  
  'MM-0401': { id: 'MM-0401', code: 'MM-0401', nameHe: 'אפור צפחה', nameEn: 'Slate Grey', hex: '#8E9291' },
  'MM-0412': { id: 'MM-0412', code: 'MM-0412', nameHe: 'אפור יער', nameEn: 'Forest Grey', hex: '#7A8275' },
  'MM-0423': { id: 'MM-0423', code: 'MM-0423', nameHe: 'אפור טוף', nameEn: 'Tuff Grey', hex: '#7A7B7A' },
  
  'MM-0501': { id: 'MM-0501', code: 'MM-0501', nameHe: 'אפור בזלת', nameEn: 'Basalt Grey', hex: '#756E5E' },
  'MM-0512': { id: 'MM-0512', code: 'MM-0512', nameHe: 'אפור עופרת', nameEn: 'Lead Grey', hex: '#6B6E6C' },
  
  'MM-0601': { id: 'MM-0601', code: 'MM-0601', nameHe: 'צפחה כהה', nameEn: 'Dark Slate', hex: '#4E5452' },
  'MM-0612': { id: 'MM-0612', code: 'MM-0612', nameHe: 'אפור גרפיט', nameEn: 'Graphite Grey', hex: '#373F43' },
};

// Legacy support - keep MICROLIT_COLORS for backward compatibility
export const MICROLIT_COLORS = NATURAL_MINERALS;

// Primer types
export const PRIMERS = {
  solan: {
    id: 'primer-solan',
    nameHe: 'פריימר סולן',
    nameEn: 'Primer Solan',
    descriptionHe: 'לתשתית סופגת - בטון, גבס, חימר, עץ',
    descriptionEn: 'For absorbent substrates - concrete, gypsum, clay, wood',
    forAbsorbent: true,
    color: '#D4C4A8',
  },
  quartz: {
    id: 'primer-quartz',
    nameHe: 'פריימר קוורץ',
    nameEn: 'Primer Quartz',
    descriptionHe: 'לתשתית לא סופגת - פלסטיק, PLA, מתכת, זכוכית',
    descriptionEn: 'For non-absorbent substrates - plastic, PLA, metal, glass',
    forAbsorbent: false,
    color: '#E8E4D9',
  },
};

// Substrate types and their classifications
export const SUBSTRATES = {
  pla: {
    id: 'pla',
    nameHe: 'PLA / PLA+',
    nameEn: 'PLA / PLA+',
    descriptionHe: 'פלסטיק הדפסה תלת מימדית סטנדרטי',
    descriptionEn: 'Standard 3D printing plastic',
    icon: 'Printer',
    type: 'plastic',
    absorbent: false,
    primer: 'quartz',
    riskLevel: 'low',
    layers: 2,
  },
  prints3d: {
    id: 'prints3d',
    nameHe: 'יצירות תלת מימד',
    nameEn: '3D Creations',
    descriptionHe: 'ABS, PETG, ניילון, רזין ופלסטיק אחר',
    descriptionEn: 'ABS, PETG, Nylon, Resin and other plastics',
    icon: 'Box',
    type: 'plastic',
    absorbent: false,
    primer: 'quartz',
    riskLevel: 'low',
    layers: 2,
    noteHe: 'יש להשתמש בפריימר קוורץ',
    noteEn: 'Use Quartz primer for best results',
  },
  concrete: {
    id: 'concrete',
    nameHe: 'בטון',
    nameEn: 'Concrete',
    descriptionHe: 'משטחי בטון יצוק',
    descriptionEn: 'Cast concrete surfaces',
    icon: 'Cuboid',
    type: 'absorbent',
    absorbent: true,
    primer: 'solan',
    riskLevel: 'low',
    layers: 2,
  },
  gypsum: {
    id: 'gypsum',
    nameHe: 'גבס',
    nameEn: 'Gypsum',
    descriptionHe: 'לוחות גבס, קירות, תקרות',
    descriptionEn: 'Gypsum boards, walls, ceilings',
    icon: 'Square',
    type: 'absorbent',
    absorbent: true,
    primer: 'solan',
    riskLevel: 'low',
    layers: 2,
  },
  clay: {
    id: 'clay',
    nameHe: 'חימר',
    nameEn: 'Clay',
    descriptionHe: 'חימר גולמי, חרס לא שרוף',
    descriptionEn: 'Raw clay, unfired pottery',
    icon: 'Amphora',
    type: 'absorbent',
    absorbent: true,
    primer: 'solan',
    riskLevel: 'low',
    layers: 2,
  },
  ceramic: {
    id: 'ceramic',
    nameHe: 'קרמיקה',
    nameEn: 'Ceramic',
    descriptionHe: 'קרמיקה שרופה, חרסינה',
    descriptionEn: 'Fired ceramics, porcelain',
    icon: 'Gem',
    type: 'smooth',
    absorbent: false,
    primer: 'quartz',
    riskLevel: 'low',
    layers: 2,
  },
  wood: {
    id: 'wood',
    nameHe: 'MDF / עץ',
    nameEn: 'MDF / Wood',
    descriptionHe: 'לוחות MDF, עץ לבוד, או פניילים',
    descriptionEn: 'MDF boards, plywood, or wood panels',
    icon: 'TreeDeciduous',
    type: 'absorbent',
    absorbent: true,
    primer: 'solan',
    riskLevel: 'low',
    layers: 2,
  },
  foam: {
    id: 'foam',
    nameHe: 'קלקר / EPS',
    nameEn: 'Foam / EPS',
    descriptionHe: 'קצף פוליסטירן מוקצף',
    descriptionEn: 'Expanded polystyrene foam',
    icon: 'Layers',
    type: 'foam',
    absorbent: false,
    primer: 'quartz',
    riskLevel: 'medium',
    layers: 3,
    warningHe: 'יש להימנע מממסים! השתמש בשכבות דקות בלבד',
    warningEn: 'Avoid solvents! Use thin layers only',
  },
  metal: {
    id: 'metal',
    nameHe: 'מתכת',
    nameEn: 'Metal',
    descriptionHe: 'משטחים מתכתיים מוכנים',
    descriptionEn: 'Prepared metal surfaces',
    icon: 'Shield',
    type: 'smooth',
    absorbent: false,
    primer: 'quartz',
    riskLevel: 'medium',
    layers: 2,
    warningHe: 'נדרש שיוף ופריימר קוורץ להידבקות',
    warningEn: 'Requires sanding and Quartz primer for adhesion',
  },
  glass: {
    id: 'glass',
    nameHe: 'זכוכית',
    nameEn: 'Glass',
    descriptionHe: 'משטחי זכוכית מוכנים',
    descriptionEn: 'Prepared glass surfaces',
    icon: 'Gem',
    type: 'smooth',
    absorbent: false,
    primer: 'quartz',
    riskLevel: 'high',
    layers: 2,
    warningHe: 'נדרש פריימר קוורץ והכנת משטח יסודית',
    warningEn: 'Requires Quartz primer and thorough surface preparation',
  },
  mixed: {
    id: 'mixed',
    nameHe: 'שילוב תשתיות',
    nameEn: 'Mixed Substrates',
    descriptionHe: 'יצירה עם מספר חומרים שונים',
    descriptionEn: 'Creation with multiple different materials',
    icon: 'Layers',
    type: 'mixed',
    absorbent: false,
    primer: 'quartz',
    riskLevel: 'medium',
    layers: 2,
    forceTextured: true,
    noteHe: 'פריימר קוורץ + מיקרומינרל סמיך מומלץ',
    noteEn: 'Quartz primer + textured MicroMineral recommended',
  },
  notSure: {
    id: 'not-sure',
    nameHe: 'אחר / לא בטוח',
    nameEn: 'Other / Not Sure',
    descriptionHe: 'לא בטוח לגבי סוג המשטח שלי',
    descriptionEn: 'Not sure about my surface type',
    icon: 'HelpCircle',
    type: 'unknown',
    absorbent: false,
    primer: 'quartz',
    riskLevel: 'high',
    layers: 2,
    warningHe: 'אנא צור קשר לייעוץ לפני היישום',
    warningEn: 'Please contact us for advice before application',
  },
};

// Application steps based on mart-mineral.com instructions - FULL PROCESS
export const APPLICATION_STEPS = {
  sanding: {
    id: 'sanding',
    orderHe: 'שלב 1',
    orderEn: 'Step 1',
    titleHe: 'שיוף',
    titleEn: 'Sanding',
    descriptionHe: 'שפשף את המשטח בעדינות עם נייר זכוכית 220-320. השיוף יוצר משטח מחוספס להידבקות מיטבית.',
    descriptionEn: 'Gently sand the surface with 220-320 grit sandpaper. Sanding creates a roughened surface for optimal adhesion.',
    durationHe: '5-10 דקות',
    durationEn: '5-10 minutes',
    icon: 'Eraser',
    tips: {
      he: ['השתמש בנייר 220-320 לתוצאה מיטבית', 'שפשף בתנועות עגוליות אחידות'],
      en: ['Use 220-320 grit for best results', 'Sand in uniform circular motions'],
    },
  },
  cleaning: {
    id: 'cleaning',
    orderHe: 'שלב 2',
    orderEn: 'Step 2',
    titleHe: 'ניקוי',
    titleEn: 'Cleaning',
    descriptionHe: 'נקה את המשטח היטב מאבק, שמן ולכלוך. השתמש באלכוהול או מים וסבון ותן לייבש לחלוטין.',
    descriptionEn: 'Thoroughly clean the surface from dust, oil and dirt. Use alcohol or water and soap, then let dry completely.',
    durationHe: '5-15 דקות',
    durationEn: '5-15 minutes',
    icon: 'Sparkles',
    tips: {
      he: ['ודא שהמשטח יבש לחלוטין', 'הימנע ממגע בידיים שמנוניות'],
      en: ['Ensure surface is completely dry', 'Avoid touching with oily hands'],
    },
  },
  primer: {
    id: 'primer',
    orderHe: 'שלב 3',
    orderEn: 'Step 3',
    titleHe: 'מריחת פריימר',
    titleEn: 'Primer Application',
    descriptionHe: 'מרח שכבת פריימר מתאימה לתשתית. פריימר סולן לתשתית סופגת, פריימר קוורץ לתשתית לא סופגת.',
    descriptionEn: 'Apply appropriate primer layer. Solan primer for absorbent substrates, Quartz primer for non-absorbent substrates.',
    durationHe: '10-15 דקות מריחה',
    durationEn: '10-15 minutes application',
    icon: 'Paintbrush2',
    critical: true,
    tips: {
      he: ['בחר פריימר לפי סוג התשתית', 'המתן 2-4 שעות לייבוש'],
      en: ['Choose primer based on substrate type', 'Wait 2-4 hours for drying'],
    },
  },
  firstCoat: {
    id: 'first-coat',
    orderHe: 'שלב 4',
    orderEn: 'Step 4',
    titleHe: 'שכבה ראשונה - מיקרומינרל',
    titleEn: 'First Coat - MicroMineral',
    descriptionHe: 'מרח שכבה ראשונה של מיקרומינרל. לטקסטורה - השתמש בחומר סמיך. לגימור חלק - השתמש בחומר דליל יותר.',
    descriptionEn: 'Apply first coat of MicroMineral. For texture - use thicker consistency. For smooth finish - use more diluted product.',
    durationHe: '15-30 דקות',
    durationEn: '15-30 minutes',
    icon: 'Paintbrush',
    tips: {
      he: ['לטקסטורה: חומר סמיך יותר', 'לגימור חלק: חומר דליל יותר', 'עבוד בתנועות אחידות'],
      en: ['For texture: use thicker material', 'For smooth: use thinner material', 'Work in uniform strokes'],
    },
  },
  drying: {
    id: 'drying',
    orderHe: 'שלב 5',
    orderEn: 'Step 5',
    titleHe: 'ייבוש בין שכבות',
    titleEn: 'Drying Between Coats',
    descriptionHe: 'יבש למגע: ~30 דקות. המתן 12-24 שעות לפני מריחת שכבה נוספת.',
    descriptionEn: 'Touch dry: ~30 minutes. Wait 12-24 hours before applying additional coat.',
    durationHe: '12-24 שעות',
    durationEn: '12-24 hours',
    icon: 'Clock',
    critical: true,
    tips: {
      he: ['אל תאיץ ייבוש עם חום', 'וודא אוורור טוב'],
      en: ['Do not speed up drying with heat', 'Ensure good ventilation'],
    },
  },
  secondCoat: {
    id: 'second-coat',
    orderHe: 'שלב 6',
    orderEn: 'Step 6',
    titleHe: 'שכבה סופית - מיקרומינרל דליל',
    titleEn: 'Final Coat - Diluted MicroMineral',
    descriptionHe: 'מרח שכבה סופית של מיקרומינרל דליל לגימור אחיד וחלק.',
    descriptionEn: 'Apply final coat of diluted MicroMineral for uniform, smooth finish.',
    durationHe: '15-30 דקות',
    durationEn: '15-30 minutes',
    icon: 'Layers',
    tips: {
      he: ['השתמש בחומר דליל יותר', 'שכבה דקה ואחידה'],
      en: ['Use more diluted material', 'Thin and uniform layer'],
    },
  },
  curing: {
    id: 'curing',
    orderHe: 'שלב 7',
    orderEn: 'Step 7',
    titleHe: 'אשפרה מלאה',
    titleEn: 'Full Curing',
    descriptionHe: 'פולימריזציה מלאה: 7 ימים. לאחר מכן עמיד לאור UV ותנאי מזג אוויר.',
    descriptionEn: 'Full polymerization: 7 days. Afterward, resistant to UV light and weather conditions.',
    durationHe: '7 ימים',
    durationEn: '7 days',
    icon: 'Shield',
    critical: true,
    tips: {
      he: ['הימנע ממים ב-24 השעות הראשונות', 'ניתן לשייף לאחר אשפרה מלאה'],
      en: ['Avoid water in first 24 hours', 'Can be sanded after full cure'],
    },
  },
  sealing: {
    id: 'sealing',
    orderHe: 'שלב 8 (אופציונלי)',
    orderEn: 'Step 8 (Optional)',
    titleHe: 'סילר - איטום מגן',
    titleEn: 'Sealer - Protective Coating',
    descriptionHe: 'להגנה מקסימלית - מרח סילר שקוף לאחר אשפרה מלאה. מומלץ לשימוש חיצוני או בתנאי רטיבות.',
    descriptionEn: 'For maximum protection - apply clear sealer after full curing. Recommended for outdoor use or wet conditions.',
    durationHe: 'לאחר 7 ימים',
    durationEn: 'After 7 days',
    icon: 'Droplets',
    optional: true,
    tips: {
      he: ['מומלץ לשימוש חיצוני', 'מגן מפני מים ולכלוך'],
      en: ['Recommended for outdoor use', 'Protects from water and dirt'],
    },
  },
};

// Technical specifications from mart-mineral.com
export const TECHNICAL_SPECS = {
  thickness: '≤2mm total',
  finish: 'Matte / Mineral',
  recoatingTime: '12-24 hours',
  sandable: true,
  waterResistance: 'High (can apply sealer after cure)',
  uvResistance: 'Excellent',
  vocContent: 'Very low',
  base: 'Mineral co-polymer hybrid',
  shelfLife: '12 months (unopened)',
  storageTemp: '10-30°C',
};

// WhatsApp contact number from mart-mineral.com style
export const CONTACT = {
  whatsapp: '972505615605',
  whatsappDisplay: '050-561-5605',
  company: 'Shahal Building Materials Ltd.',
  companyHe: 'שחל - חומרי בניין בע"מ',
  brand: "M'art Professional Series",
  since: 1958,
};
